import Select from "react-select";
import Switch from "react-switch";
import i18n from "../../language/i18n";
import { useEffect, useRef, useState } from "react";
import { IInputGroupProps } from "../../properties/GroupProperties";
import { IOptionProps } from "../../properties/CommonProperties";
import BaseModal from "../modals/BaseModal";
import InputWithSpecialExpression from "../texts/InputWithSpecialExpression";

const InputGroup = ({
  type,
  title,
  defaultValue,
  placeholder,
  value,
  minValue,
  maxValue,
  onFocus,
  onChange,
  onClick,
  onKeyDown,
  optionList,
  errorText,
  multiple,
  accept,
  theme,
  useMinHeight,
  disabled,
  limit,
  useMath,
  checked,
  description,
}: IInputGroupProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [showMathConstructor, setShowMathConstructor] =
    useState<boolean>(false);

  useEffect(() => {
    if (!textAreaRef) return;
    if (!textAreaRef.current) return;
    if (value) {
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    } else {
      textAreaRef.current.style.height = `44px`;
    }
  }, [textAreaRef, value]);

  const retrieveNullableOptionList = () => {
    if (!optionList) return [];
    const currentOptionList = {
      text: i18n.t("option_please_select"),
      value: "DEFAULT_OPTION",
    };
    return [currentOptionList, ...optionList];
  };

  const convertOptionListToSelectComponent = (optionList: IOptionProps[]) => {
    return optionList.map((option: IOptionProps) => ({
      value: option.value,
      label: option.text,
    }));
  };

  // `limit` blocks the keystroke instead of truncating, so nothing the learner typed vanishes
  // behind their cursor. The last clause is what keeps it escapable: a value can already be OVER
  // the limit (a bio saved from the profile screen before it was capped), and gating purely on
  // `<= limit` left every intermediate value over the limit too — every keystroke swallowed, the
  // box read-only except by select-all-and-replace. Getting SHORTER is always allowed.
  const acceptsLength = (next: string) =>
    !limit || next.length <= limit || next.length < String(value ?? "").length;

  const handleTextOnChange = (e: any) => {
    if (acceptsLength(String(e.target.value ?? ""))) onChange && onChange(e);
  };

  const handleTextAreaOnChange = (e: any) => {
    if (!textAreaRef) return;
    if (!textAreaRef.current) return;
    textAreaRef.current.style.height = `30px`;
    textAreaRef.current.style.height = `${e.target.scrollHeight + 6}px`;
    if (acceptsLength(String(e.target.value ?? ""))) onChange && onChange(e);
  };

  const handleOpenMathConstructor = () => {
    setShowMathConstructor(true);
  };

  const MathConstructorModal = () => {
    return (
      <BaseModal
        title={i18n.t("math_editor")}
        icon="🧮"
        isOpen={showMathConstructor}
        size="small"
        onAfterOpen={() => {}}
        onRequestClose={() => {
          setShowMathConstructor(false);
        }}
      >
        <div className="ig-math-wrap">
          <div className="ig-math-stack">
            <p className="ig-math-hint">
              {i18n.t("write_equation_in_double_dollars")}
            </p>
            <div className="ig-math-table-wrap">
              <table className="ig-math-table">
                <thead>
                  <tr className="ig-math-thead">
                    <th className="ig-math-th">LaTeX</th>
                    <th className="ig-math-th"></th>
                  </tr>
                </thead>
                <tbody className="ig-math-tbody">
                  <tr>
                    <td className="ig-math-td">{"\\frac{a}{b}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\frac{a}{b}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"x^{2}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$x^{2}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"x_{i}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$x_{i}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\sqrt{x}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\sqrt{x}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\sqrt[3]{x}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\sqrt[3]{x}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\sum_{i=1}^{n}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression
                        value={"$$\\sum_{i=1}^{n}$$"}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\int_{a}^{b}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\int_{a}^{b}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\infty"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\infty$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\pi"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\pi$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\alpha \\beta \\gamma"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression
                        value={"$$\\alpha \\beta \\gamma$$"}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\leq \\geq \\neq"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression
                        value={"$$\\leq \\geq \\neq$$"}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\pm \\times \\div"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression
                        value={"$$\\pm \\times \\div$$"}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\log_{a}{b}"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression value={"$$\\log_{a}{b}$$"} />
                    </td>
                  </tr>
                  <tr>
                    <td className="ig-math-td">{"\\sin \\cos \\tan"}</td>
                    <td className="ig-math-td">
                      <InputWithSpecialExpression
                        value={"$$\\sin \\cos \\tan$$"}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  const CheckboxInputGroup = () => {
    return (
      <div
        className="ig-checkbox"
        onClick={onClick}
      >
        <span className={`ig-cbx-box ${value ? "ig-cbx-on" : ""}`}>
          {value ? (
            <span className="ig-cbx-check">
              <svg viewBox="0 0 14 14" width={11} height={11}>
                <path
                  d="M2 7.2l3 3 7-7.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : null}
        </span>
        <p>{title}</p>
      </div>
    );
  };

  const FileInputGroup = () => {
    return (
      <div className="ig-field">
        {title ? (
          <p className="ig-label">
            {title}
          </p>
        ) : null}
        <input
          className="ig-input"
          type={type}
          defaultValue={defaultValue}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onClick={onClick}
          multiple={multiple}
          accept={accept}
        />
      </div>
    );
  };

  const DateInputGroup = () => {
    return (
      <div className="ig-field">
        {title ? (
          <p className="ig-label">
            {title}
          </p>
        ) : null}
        <input
          className={`ig-date ${
            errorText
              ? "ig-date-error"
              : theme === "red"
                ? "ig-date-red"
                : "ig-date-default"
          }`}
          type={type}
          defaultValue={defaultValue}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          min={minValue}
          max={maxValue}
        />
      </div>
    );
  };

  const SearchableSelectInputGroup = () => {
    return (
      <div className="ig-field">
        {title ? (
          <p className="ig-label">
            {title}
          </p>
        ) : null}
        <Select
          options={convertOptionListToSelectComponent(
            retrieveNullableOptionList(),
          )}
          className="ig-wfull"
          placeholder={placeholder ? placeholder : i18n.t("option_please_select")}
          styles={{
            control: (baseStyles: any, state: any) => ({
              ...baseStyles,
              borderWidth: 1,
              borderStyle: "solid",
              borderRadius: 16,
              borderColor: errorText
                ? "#ff6b6b"
                : theme === "red"
                  ? "#ff6b6b"
                  : state.isFocused
                    ? "#73d7e6"
                    : state.isActive
                      ? "#d2dde1"
                      : "#d2dde1",
              backgroundColor: theme !== "red" ? "#ffffff" : "#ff6b6b",
              paddingRight: 6,
              paddingLeft: 6,
              paddingTop: 2,
              paddingBottom: 2,
              outline: "none",
              margin: 0,
              text: theme === "red" ? "#ffffff" : "#55777f",
              boxShadow: errorText
                ? "0px 0px 7px 0px rgba(255, 107, 107, 1)"
                : state.isFocused
                  ? "0px 0px 7px 0px rgba(115, 215, 230, 100)"
                  : "none",
              "&:hover": {
                outline: "none",
              },
            }),
            menu: (baseStyles: any, state: any) => ({
              ...baseStyles,
              borderRadius: 16,
              paddingTop: 10,
              paddingBottom: 10,
              boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.25)",
              zIndex: 1000000,
            }),
            option: (baseStyles: any, state: any) => ({
              ...baseStyles,
              color: state.isSelected ? "#2b3a41" : "#2b3a41",
              backgroundColor: state.isSelected ? "#eaecf1" : "",
              "&:hover": {
                backgroundColor: "#eaecf1",
              },
              paddingLeft: 20,
              paddingRight: 20,
              fontSize: 14,
              fontFamily: "'Quicksand', sans-serif",
            }),
            singleValue: (baseStyles: any) => ({
              ...baseStyles,
              fontSize: 14,
              fontFamily: "'Quicksand', sans-serif",
              color: "#2b3a41",
            }),
            placeholder: (baseStyles: any) => ({
              ...baseStyles,
              fontSize: 14,
              fontFamily: "'Quicksand', sans-serif",
              color: "#A7BFC4",
            }),
            input: (baseStyles: any) => ({
              ...baseStyles,
              fontSize: 14,
              fontFamily: "'Quicksand', sans-serif",
            }),
            dropdownIndicator: (baseStyles: any, state: any) => ({
              ...baseStyles,
              color: state.isFocused ? "#57c2d3" : "#55777f",
              "&:hover": {
                color: state.isFocused ? "#57c2d3" : "#55777f",
              },
            }),
            menuPortal: (baseStyles: any) => ({
              ...baseStyles,
              zIndex: 1000000,
            }),
          }}
          menuPortalTarget={document.body}
          components={{
            IndicatorSeparator: () => null,
          }}
          value={convertOptionListToSelectComponent(
            retrieveNullableOptionList(),
          ).find((option) => option.value === value)}
          onChange={(target: any) => {
            const event = { target };
            onChange && onChange(event);
          }}
        />
      </div>
    );
  };

  const TextAreaInputGroup = () => {
    return (
      <div className="ig-ta-wrap">
        <div className="ig-row-between">
          <div>
            {title ? (
              <p className="ig-label">
                {title}
              </p>
            ) : null}
          </div>
          <div>
            {value && limit ? (
              <p className={`ig-count ${value.length > limit ? "is-over" : ""}`}>
                {value.length} / {limit}
              </p>
            ) : null}
          </div>
        </div>

        <textarea
          ref={textAreaRef}
          disabled={disabled}
          className={`ig-ta ${
            useMinHeight ? "ig-ta-minh" : ""
          } ${
            errorText ? "ig-ta-error" : "ig-ta-default"
          } ${
            disabled ? "ig-ta-disabled" : ""
          } ${useMath ? "ig-pr20" : ""}`}
          placeholder={errorText ? errorText : placeholder}
          value={value}
          onChange={handleTextAreaOnChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
        />

        {useMath && (
          <button
            className="ig-math-btn ig-z10"
            onClick={handleOpenMathConstructor}
            type="button"
          >
            <div className="ig-math-btn-inner">
              <span className="ig-math-btn-label">𝑓(x)</span>
              <svg
                className="ig-icon3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
          </button>
        )}
      </div>
    );
  };

  const TextInputGroup = () => {
    return (
      <div className="ig-text-wrap">
        <div className="ig-row-between">
          <div>
            {title ? (
              <p className="ig-label">
                {title}
              </p>
            ) : null}
          </div>
          <div>
            {value && limit ? (
              <p className={`ig-count ${value.length > limit ? "is-over" : ""}`}>
                {value.length} / {limit}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className={`ig-text-box ${
            errorText ? "ig-text-box-error" : "ig-text-box-default"
          } ${
            disabled ? "ig-text-box-disabled" : "ig-text-box-enabled"
          }`}
        >
          <input
            disabled={disabled}
            className={`ig-text-input ${
              disabled ? "ig-text-input-disabled" : ""
            } ${
              errorText ? "ig-text-input-error" : ""
            } ${useMath ? "ig-pr20" : ""}`}
            type={type}
            defaultValue={defaultValue}
            placeholder={errorText ? errorText : placeholder}
            value={value}
            onChange={handleTextOnChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            min={minValue}
            max={maxValue}
          />
        </div>

        {useMath && (
          <button
            className="ig-math-btn"
            onClick={handleOpenMathConstructor}
            type="button"
          >
            <div className="ig-math-btn-inner">
              <span className="ig-math-btn-label">𝑓(x)</span>
              <svg
                className="ig-icon3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
          </button>
        )}
      </div>
    );
  };

  const SwitchInputGroup = () => {
    return (
      <div className="ig-switch-wrap">
        <div className="ig-switch-row">
          <Switch
            onChange={() => {
              onClick && onClick(!checked);
            }}
            checkedIcon={false}
            offColor={"#e2e8f0"}
            onColor={"#57C2D3"}
            offHandleColor={"#57C2D3"}
            onHandleColor={"#e2e8f0"}
            uncheckedIcon={false}
            checked={!!checked}
            height={20}
            width={40}
            handleDiameter={14}
            disabled={disabled}
          />
          <p
            className={`${
              checked ? "ig-switch-on" : "ig-switch-off"
            } ig-switch-label`}
          >
            {title}
          </p>
        </div>
        {description ? (
          <p className="ig-switch-desc">
            {description}
          </p>
        ) : null}
      </div>
    );
  };

  const RenderMainContent = () => {
    if (type === "text" || type === "number") {
      return TextInputGroup();
    } else if (type === "textarea") {
      return TextAreaInputGroup();
    } else if (type === "password") {
      return TextInputGroup();
    } else if (type === "select") {
      return SearchableSelectInputGroup();
    } else if (type === "date" || type === "datetime-local") {
      return DateInputGroup();
    } else if (type === "file") {
      return FileInputGroup();
    } else if (type === "checkbox") {
      return CheckboxInputGroup();
    } else if (type === "switch") {
      return SwitchInputGroup();
    }
  };

  return (
    <>
      {RenderMainContent()}
      <MathConstructorModal />
    </>
  );
};

export default InputGroup;
